import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, FormGroup, Label, Input, Button } from 'reactstrap'
import { getIdData, addData, updateData } from '../../lib/courselesson'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { StudentTopMenuLayout } from '../../Layout/StudentTopMenu/StudentTopMenuLayout'
import { CourseDepartmentSelect, CourseLevelSelect, CourseApScoreSelect, ClassForTranskriptSelect } from '../Common/FormSelects'

const StudentLessonEdit = () => {
	const [coursename, setCoursename] = useState('')
	const [department, setDepartment] = useState('')
	const [level, setLevel] = useState('')
	const [apscore, setApscore] = useState(0)
	const [grade, setGrade] = useState('')
	const [credit, setCredit] = useState('')
	const [courseyear, setCourseyear] = useState(9)
	const [isgpacalc, setIsgpacalc] = useState(true)
	const [loading, setLoading] = useState(false)

	const navigate = useNavigate()
	const { status, studentId: paramStudentId } = useParams()
	const { user } = useAuth()
	const isInstructorView = user?.role === 'Instructor'
	const studentId = isInstructorView ? paramStudentId : user?.id

	useEffect(() => {
		if ((status || '').toLowerCase() === 'add') {
			if (!department) setDepartment('English')
			if (!level) setLevel('AP')
		}

		if (status !== 'Add') {
			loadData()
		}
	}, [status])

	const loadData = async () => {
		try {
			setLoading(true)
			const data = await getIdData(status)
			if (data && data.length > 0) {
				const item = data[0]
				setCoursename(item.coursename)
				setDepartment(item.department)
				setLevel(item.level)
				setApscore(item.apscore)
				setGrade(item.grade)
				setCredit(item.credit)
				setCourseyear(item.courseyear)
				setIsgpacalc(item.isgpacalc)
			}
		} catch (error) {
			console.error('Error fetching lesson data:', error)
		} finally {
			setLoading(false)
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)

		try {
			const payload = {
				coursename,
				department,
				level,
				apscore: level === 'AP' ? apscore : 0,
				grade: parseFloat(grade) || 0,
				credit: parseFloat(credit) || 0,
				courseyear,
				isgpacalc,
				studentId
			}

			if (status === 'Add') {
				await addData(payload)
			} else {
				await updateData({ ...payload, id: status })
			}
			navigate(-1)
		} catch (error) {
			console.error('Error saving lesson:', error)
		} finally {
			setLoading(false)
		}
	}

	return (
		<StudentTopMenuLayout activeMenu="Lessons" pageName="LessonEdit" showPageNotes={false}>
			<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
				<div className="card-header bg-white border-bottom">
					<h5 className="mb-0 text-dark">{status === 'Add' ? 'Add New Lesson' : 'Update Lesson'}</h5>
				</div>
				<div className="card-body bg-white">
					{loading && status !== 'Add' ? (
						<div className="text-center p-4">
							<div className="spinner-border" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
							<p className="mt-2">Loading course data...</p>
						</div>
					) : (
						<Form onSubmit={handleSubmit}>
							<div className="row">
								<div className="col-lg-9 col-md-12">
									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold">Course Name</Label>
										<Input
											type="text"
											value={coursename}
											onChange={(e) => setCoursename(e.target.value)}
											className="form-control"
											placeholder="Enter Course Name"
											required
										/>
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold">Department</Label>
										<CourseDepartmentSelect value={department} setValue={setDepartment} />
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold">Course Level</Label>
										<CourseLevelSelect value={level} setValue={setLevel} />
									</FormGroup>

									{level === 'AP' && (
										<FormGroup className="mb-3">
											<Label className="form-control-label fw-bold">AP Test Score</Label>
											<CourseApScoreSelect value={apscore} setValue={setApscore} />
										</FormGroup>
									)}

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold">Grade</Label>
										<Input
											type="number"
											value={grade}
											onChange={(e) => setGrade(e.target.value)}
											className="form-control"
											placeholder="Enter Grade (0-100)"
											min="0"
											max="100"
										/>
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold">Credit</Label>
										<Input
											type="number"
											step="0.25"
											value={credit}
											onChange={(e) => setCredit(e.target.value)}
											className="form-control"
											placeholder="Enter Credit"
											min="0"
										/>
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold">GPA Calculation</Label>
										<div className="d-flex gap-3">
											<div className="form-check">
												<Input
													className="form-check-input"
													type="radio"
													name="isgpacalc"
													id="gpaYes"
													checked={isgpacalc}
													onChange={() => setIsgpacalc(true)}
												/>
												<Label className="form-check-label" for="gpaYes">
													Yes
												</Label>
											</div>
											<div className="form-check">
												<Input
													className="form-check-input"
													type="radio"
													name="isgpacalc"
													id="gpaNo"
													checked={!isgpacalc}
													onChange={() => setIsgpacalc(false)}
												/>
												<Label className="form-check-label" for="gpaNo">
													No
												</Label>
											</div>
										</div>
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold">High School Year</Label>
										<ClassForTranskriptSelect value={courseyear} setValue={setCourseyear} />
									</FormGroup>

									<div className="d-flex justify-content-between gap-2">
										<Button type="submit" color="primary" disabled={loading}>
											<i className="fa fa-save me-2"></i>
											{loading ? 'Saving...' : status === 'Add' ? 'Add Course' : 'Update Course'}
										</Button>
										<Button type="button" color="secondary" onClick={() => navigate(-1)}>
											<i className="fa fa-times me-2"></i>
											Cancel
										</Button>
									</div>
								</div>
							</div>
						</Form>
					)}
				</div>
			</div>
		</StudentTopMenuLayout>
	)
}

export { StudentLessonEdit }
